import validationConfig from '../../../../validation-config.json';
import type { QuestionEditorState } from '../types';
import { OPTION_BASED_TYPES, INPUT_FORMAT_TYPES, LENGTH_VALIDATED_TYPES } from '../constants';

export interface ValidationErrors {
  questionTitle?: string;
  questionTypeId?: string;
  surveyQuestionDbCode?: string;
  inputFormat?: string;
  minLength?: string;
  maxLength?: string;
  helpLink?: string;
  options?: string;
}

const DB_CODE_PATTERN = /^[a-zA-Z0-9_]+$/;
const URL_PATTERN = /^https?:\/\/.+/;

const ADMIN_QUESTIONS_VALIDATION = {
  questionTitle: {
    required: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['questionTitle']?.['required'] ?? 'Question title is required',
  },
  questionTypeId: {
    required: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['questionTypeId']?.['required'] ?? 'Question type is required',
  },
  surveyQuestionDbCode: {
    required: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['surveyQuestionDbCode']?.['required'] ?? 'DB code is required',
    invalidFormat: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['surveyQuestionDbCode']?.['invalidFormat'] ?? 'Only letters, numbers, and underscores allowed',
  },
  inputFormat: {
    notApplicable: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['inputFormat']?.['notApplicable'] ?? 'Input format is not applicable for the selected question type',
  },
  minLength: {
    required: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['minLength']?.['required'] ?? 'Minimum length is required',
    invalidFormat: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['minLength']?.['invalidFormat'] ?? 'Must be a positive number',
  },
  maxLength: {
    required: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['maxLength']?.['required'] ?? 'Maximum length is required',
    invalidFormat: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['maxLength']?.['invalidFormat'] ?? 'Must be a positive number',
    outOfRange: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['maxLength']?.['outOfRange'] ?? 'Must be ≥ minimum length',
  },
  helpLink: {
    invalidFormat: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['helpLink']?.['invalidFormat'] ?? 'Must be a valid URL starting with http:// or https://',
  },
  options: {
    minRequired: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['options']?.['minRequired'] ?? 'At least two options are required',
    allRequired: (validationConfig as Record<string, unknown>)['ADMIN_QUESTIONS_PAGE']?.['_defaults']?.['options']?.['allRequired'] ?? 'All option titles are required',
  },
};

export function validateEditorState(state: QuestionEditorState): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!state.questionTitle.trim()) {
    errors.questionTitle = ADMIN_QUESTIONS_VALIDATION.questionTitle.required;
  }

  if (!state.questionTypeId) {
    errors.questionTypeId = ADMIN_QUESTIONS_VALIDATION.questionTypeId.required;
  }

  if (state.surveyQuestionDbCode.trim() && !DB_CODE_PATTERN.test(state.surveyQuestionDbCode)) {
    errors.surveyQuestionDbCode = ADMIN_QUESTIONS_VALIDATION.surveyQuestionDbCode.invalidFormat;
  }
  if (!state.surveyQuestionDbCode.trim()) {
    errors.surveyQuestionDbCode = ADMIN_QUESTIONS_VALIDATION.surveyQuestionDbCode.required;
  }

  const typeId = state.questionTypeId;

  if (typeId && INPUT_FORMAT_TYPES.has(typeId) === false && state.inputFormat) {
    errors.inputFormat = ADMIN_QUESTIONS_VALIDATION.inputFormat.notApplicable;
  }

  if (typeId && LENGTH_VALIDATED_TYPES.has(typeId)) {
    if (!state.minLength) {
      errors.minLength = ADMIN_QUESTIONS_VALIDATION.minLength.required;
    } else if (!/^\d+$/.test(state.minLength) || parseInt(state.minLength, 10) < 1) {
      errors.minLength = ADMIN_QUESTIONS_VALIDATION.minLength.invalidFormat;
    }
    if (!state.maxLength) {
      errors.maxLength = ADMIN_QUESTIONS_VALIDATION.maxLength.required;
    } else if (!/^\d+$/.test(state.maxLength) || parseInt(state.maxLength, 10) < 1) {
      errors.maxLength = ADMIN_QUESTIONS_VALIDATION.maxLength.invalidFormat;
    }
    if (state.minLength && state.maxLength) {
      if (parseInt(state.minLength) > parseInt(state.maxLength)) {
        errors.maxLength = ADMIN_QUESTIONS_VALIDATION.maxLength.outOfRange;
      }
    }
  }

  if (state.helpLink.trim() && !URL_PATTERN.test(state.helpLink.trim())) {
    errors.helpLink = ADMIN_QUESTIONS_VALIDATION.helpLink.invalidFormat;
  }

  if (typeId && OPTION_BASED_TYPES.has(typeId)) {
    const activeOptions = state.options.filter((o) => {return o.active !== false});
    if (activeOptions.length < 2) {
      errors.options = ADMIN_QUESTIONS_VALIDATION.options.minRequired;
    }
    if (activeOptions.some((o) => {return !o.optionTitle.trim()})) {
      errors.options = ADMIN_QUESTIONS_VALIDATION.options.allRequired;
    }
  }

  return errors;
}

export function hasErrors(errors: ValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}
